import React from 'react';

import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

export interface KbdProps extends Styleable {
    /** The key or chord to render, e.g. `⌘K`, `esc`, `↵`. */
    children: React.ReactNode;
}

/**
 * EAA-1804: keyboard-key chip from the component sheet.
 *
 * `padding: 1px 6px`, radius 4, fill + inset 1px ring, 11px/600 monospace.
 * Ant Design has no equivalent primitive, so this is a plain `<kbd>` rather
 * than a wrapper — which is also the correct element semantically, and gives
 * screen readers the "keyboard input" role for free.
 *
 * The ring is an inset `box-shadow`, not a border, per the sheet's sizing
 * invariant: a border on this inline element would change its height and make
 * a line of text containing a Kbd taller than its neighbours.
 */
export function Kbd({ children, className, style }: KbdProps): React.ReactElement {
    const colors = useUIColors();

    return (
        <kbd
            className={cx(className)}
            style={{
                display: 'inline-flex',
                alignItems: 'center',
                padding: '1px 6px',
                borderRadius: 4,
                background: colors.fill,
                boxShadow: `inset 0 0 0 1px ${colors.border}`,
                color: colors.textMuted,
                fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                fontSize: 11,
                fontWeight: 600,
                lineHeight: 1.6,
                whiteSpace: 'nowrap',
                ...style,
            }}
        >
            {children}
        </kbd>
    );
}

export default Kbd;
